import { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../config/firebase';
import { query, getDocs, where } from 'firebase/firestore';
import { farmersCollection } from '../services/firebaseService';
import { FaEnvelope, FaLock, FaArrowLeft, FaSignInAlt, FaUserPlus, FaIdCard, FaPhone } from 'react-icons/fa';
import '../style.css';

function Login({ onLogin, onBack }) {
  const [credentials, setCredentials] = useState({
    farmerCode: '',
    phoneNumber: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loginMode, setLoginMode] = useState('farmer'); // 'farmer' or 'admin'

  useEffect(() => {
    // No need to create demo account automatically
    // Admin can sign up manually if needed
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleFarmerLogin = async () => {
    if (!credentials.farmerCode || !credentials.phoneNumber) {
      setError('Please enter both farmer code and phone number');
      return;
    }

    try {
      // Search for farmer in Firebase by code and phone number
      const q = query(
        farmersCollection, 
        where('code', '==', credentials.farmerCode),
        where('mobile', '==', credentials.phoneNumber)
      );
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        setError('Invalid farmer code or phone number');
        return;
      }

      // Get the first matching farmer
      const farmerDoc = querySnapshot.docs[0];
      const farmer = { id: farmerDoc.id, ...farmerDoc.data() };

      // Store farmer login info
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('loginMode', 'farmer');
      localStorage.setItem('farmerCode', farmer.code);
      localStorage.setItem('farmerName', farmer.name);
      localStorage.setItem('farmerMobile', farmer.mobile);
      localStorage.setItem('farmerVillage', farmer.village || 'N/A');
      localStorage.setItem('farmerId', farmer.id);
      localStorage.setItem('userId', farmer.userId); // Use the farmer's userId for data filtering
      
      // Clear Firebase cache to ensure fresh data for this farmer
      if (window.clearAllCache) {
        window.clearAllCache();
      }
      
      onLogin();
    } catch (error) {
      console.error('Farmer login error:', error);
      setError('Login failed. Please try again.');
    }
  };

  const handleAdminLogin = async () => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, credentials.farmerCode, credentials.phoneNumber);
      localStorage.setItem('isLoggedIn', 'true');
      localStorage.setItem('loginMode', 'admin');
      localStorage.setItem('userEmail', userCredential.user.email);
      localStorage.setItem('userId', userCredential.user.uid);
      onLogin();
    } catch (error) {
      switch (error.code) {
        case 'auth/user-not-found':
          // Try to create the admin account if it doesn't exist
          try {
            const userCredential = await createUserWithEmailAndPassword(auth, credentials.farmerCode, credentials.phoneNumber);
            localStorage.setItem('isLoggedIn', 'true');
            localStorage.setItem('loginMode', 'admin');
            localStorage.setItem('userEmail', userCredential.user.email);
            localStorage.setItem('userId', userCredential.user.uid);
            onLogin();
          } catch (createError) {
            setError('Failed to create admin account. Please try again.');
          }
          break;
        case 'auth/wrong-password':
          setError('Incorrect password.');
          break;
        case 'auth/invalid-email':
          setError('Invalid email address.');
          break;
        case 'auth/weak-password':
          setError('Password must be at least 6 characters.');
          break;
        default:
          setError('Admin login failed. Try again.');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      if (loginMode === 'farmer') {
        await handleFarmerLogin();
      } else {
        await handleAdminLogin();
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fillDemo = () => {
    if (loginMode === 'farmer') {
      // Show example format - users should replace with their actual credentials
      setCredentials({ farmerCode: 'F002', phoneNumber: '6363210636' });
    } else {
      setCredentials({ farmerCode: 'akxay@gmail.com', phoneNumber: 'Akxay123' });
    }
    setError('');
  };

  return (
    <div className="login-full-page">
      {onBack && (
        <button className="back-btn-full" onClick={onBack}>
          <FaArrowLeft /> Back
        </button>
      )}

      <div className="login-split-card">
        {/* Left Side - Demo Access */}
        <div className="login-split-left">
          <div className="split-left-content">
            <div className="brand-header">
              <span className="brand-icon">🐄</span>
              <h1>Dairy ERP</h1>
              <p>Smart Dairy Management</p>
            </div>

            <div className="demo-section">
              <div className="demo-header">
                <span>🎯</span>
                <h3>Example Login Format</h3>
              </div>
              <div className="demo-credentials">
                {loginMode === 'farmer' ? (
                  <>
                    <div className="demo-item">
                      <FaIdCard className="demo-item-icon" />
                      <div className="demo-item-content">
                        <span className="demo-label">Farmer Code</span>
                        <span className="demo-value">F001</span>
                      </div>
                    </div>
                    <div className="demo-item">
                      <FaPhone className="demo-item-icon" />
                      <div className="demo-item-content">
                        <span className="demo-label">Phone Number</span>
                        <span className="demo-value">9876543210</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="demo-item">
                      <FaEnvelope className="demo-item-icon" />
                      <div className="demo-item-content">
                        <span className="demo-label">Email</span>
                        <span className="demo-value">akxay@gmail.com</span>
                      </div>
                    </div>
                    <div className="demo-item">
                      <FaLock className="demo-item-icon" />
                      <div className="demo-item-content">
                        <span className="demo-label">Password</span>
                        <span className="demo-value">Akxay123</span>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <button className="demo-btn" onClick={fillDemo}>
                ⚡ Fill Example
              </button>
              <p style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '10px' }}>
                {loginMode === 'farmer' ? 'Use your actual farmer code and phone number' : 'Admin access only'}
              </p>
            </div>

            <div className="features-row">
              <div className="feature-chip"><span>🥛</span> Milk</div>
              <div className="feature-chip"><span>👨‍🌾</span> Farmers</div>
              <div className="feature-chip"><span>📊</span> Reports</div>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="login-split-right">
          <div className="form-header">
            <h2>{loginMode === 'farmer' ? 'Farmer Login' : 'Admin Login'}</h2>
            <p>{loginMode === 'farmer' ? 'Enter your farmer details' : 'Admin access only'}</p>
          </div>

          {/* Login Mode Toggle */}
          <div className="login-mode-toggle">
            <button 
              type="button"
              className={`mode-btn ${loginMode === 'farmer' ? 'active' : ''}`}
              onClick={() => {
                setLoginMode('farmer');
                setCredentials({ farmerCode: '', phoneNumber: '' });
                setError('');
              }}
            >
              <FaIdCard /> Farmer
            </button>
            <button 
              type="button"
              className={`mode-btn ${loginMode === 'admin' ? 'active' : ''}`}
              onClick={() => {
                setLoginMode('admin');
                setCredentials({ farmerCode: '', phoneNumber: '' });
                setError('');
              }}
            >
              <FaUserPlus /> Admin
            </button>
          </div>

          <form onSubmit={handleSubmit} className="login-form">
            {loginMode === 'farmer' ? (
              <>
                <div className="form-group">
                  <label><FaIdCard /> Farmer Code</label>
                  <input
                    type="text"
                    name="farmerCode"
                    placeholder="Enter your farmer code (e.g., F001)"
                    value={credentials.farmerCode}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label><FaPhone /> Phone Number</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    placeholder="Enter your phone number"
                    value={credentials.phoneNumber}
                    onChange={handleChange}
                    required
                  />
                </div>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label><FaEnvelope /> Email</label>
                  <input
                    type="email"
                    name="farmerCode"
                    placeholder="admin@email.com"
                    value={credentials.farmerCode}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label><FaLock /> Password</label>
                  <input
                    type="password"
                    name="phoneNumber"
                    placeholder="••••••••"
                    value={credentials.phoneNumber}
                    onChange={handleChange}
                    required
                  />
                </div>
              </>
            )}

            {error && (
              <div className="error-msg">
                <span>⚠️</span> {error}
              </div>
            )}

            <button type="submit" className="submit-btn" disabled={isLoading}>
              {isLoading ? (
                <><span className="loading-spinner"></span> Processing...</>
              ) : (
                <><FaSignInAlt /> {loginMode === 'farmer' ? 'Login as Farmer' : 'Login as Admin'}</>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Login;
